import { ApiError, UnauthenticatedError } from '@lib/api';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
} from '@tanstack/react-query';

import {
    clearEnvironmentDefault,
    clearOverride,
    createFlag,
    getFlag,
    listFlags,
    setDefault,
    setEnvironmentDefault,
    setOverride,
    updateFlag,
    type CreateFlagInput,
    type SetDefaultInput,
    type SetEnvironmentDefaultInput,
    type SetOverrideInput,
    type UpdateFlagInput,
} from '../api/flags';
import { listTenantFlags } from '../api/tenant-flags';

import type { FlagEnvironment } from '../schemas/flag';

/**
 * Server state for the flags screens.
 *
 * ## Why nothing here is optimistic
 *
 * The obvious ergonomic win — flip the toggle immediately, reconcile later — is
 * the wrong trade for this data. A flag write is version-guarded, so it can be
 * *rejected* by a colleague's concurrent change; an optimistic UI would show the
 * flag on, then silently roll it back, and an operator who had already moved on
 * would carry away the belief that they turned it on. For a control plane whose
 * whole job is "what is actually true in production", showing an unconfirmed
 * value is worse than showing a brief pending state.
 */

export const flagKeys = {
    all: ['flags'] as const,
    list: () => [...flagKeys.all, 'list'] as const,
    detail: (key: string) => [...flagKeys.all, 'detail', key] as const,
};

/**
 * Retry policy shared by every flags query.
 *
 * A 4xx is an answer, not a blip: retrying a 404 three times just delays the
 * empty state. Transport failures get one retry.
 *
 * An expired session is the same kind of answer and gets the same treatment —
 * the second 401 would be as authoritative as the first, and it delays the
 * redirect `QueryProvider` triggers off it.
 */
function retryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof UnauthenticatedError) return false;
    if (error instanceof ApiError && error.status < 500) return false;
    return failureCount < 1;
}

export function useFlags() {
    return useQuery({
        queryKey: flagKeys.list(),
        queryFn: ({ signal }) => listFlags(signal),
        retry: retryUnlessClientError,
        // Load-bearing, same as the session query: under the default
        // `networkMode: 'online'` a failure React Query reads as a connectivity
        // problem parks the query in `paused` rather than surfacing an error, and
        // a screen that renders a spinner on `isPending` would spin forever.
        networkMode: 'always',
    });
}

export function useFlag(key: string) {
    return useQuery({
        queryKey: flagKeys.detail(key),
        queryFn: ({ signal }) => getFlag(key, signal),
        retry: retryUnlessClientError,
        networkMode: 'always',
    });
}

/* ------------------------------------------------------------- mutations - */

/**
 * Every mutation invalidates **both** the list and the detail, even when it
 * looks local to one of them.
 *
 * A default write changes `version`, and the next write from either screen has
 * to send the new one — a stale list left cached would hand the operator a
 * version that is already wrong and turn their next toggle into a 409 they did
 * nothing to cause.
 */
function useFlagMutation<TArgs, TResult>(
    mutationFn: (args: TArgs) => Promise<TResult>,
    keyOf: (args: TArgs) => string,
    /**
     * Anything else the write invalidates.
     *
     * An override is reachable from two directions — one flag's list of
     * museums, and one museum's list of flags — and a write from either has to
     * refresh both, or the other screen keeps serving a row the operator just
     * changed from the one next to it.
     */
    alsoInvalidate: (args: TArgs) => readonly (readonly unknown[])[] = () => [],
): UseMutationResult<TResult, Error, TArgs> {
    const queryClient = useQueryClient();

    // Returned (not `void`ed) so the mutation stays `isPending` until the
    // refetch lands. Otherwise there is a window where the toggle is live
    // again but the row on screen still holds the old `version` — a quick
    // second click would send it and earn a 409 blaming a colleague for the
    // operator's own previous write. It is also what makes the conflict
    // panel's "the screen has been refreshed" true by the time it renders.
    const refresh = (args: TArgs) =>
        Promise.all([
            queryClient.invalidateQueries({ queryKey: flagKeys.list() }),
            queryClient.invalidateQueries({ queryKey: flagKeys.detail(keyOf(args)) }),
            ...alsoInvalidate(args).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        ]);

    return useMutation({
        mutationFn,
        onSuccess: (_result, args) => refresh(args),
        /**
         * A version conflict must refetch too.
         *
         * It is the one error that *proves* the cache is stale: the write was
         * rejected precisely because the version on screen is no longer the
         * stored one. Leaving the stale row in place would strand the operator
         * on a version every retry rejects — the same 409 forever, with the
         * error message promising a refresh that never came.
         *
         * Other failures are left alone: a 400 or a 500 says nothing about
         * whether our copy is current, and refetching would only discard the
         * screen state the operator is looking at while they read the error.
         */
        onError: (error, args) => {
            if (error instanceof ApiError && error.isVersionConflict) return refresh(args);
            return undefined;
        },
        // Never retry a write. These are not idempotent from the operator's
        // point of view — a retried create files a second audit entry, and a
        // retried version-guarded write would fail on the bumped version anyway.
        retry: false,
        networkMode: 'always',
    });
}

export function useCreateFlag() {
    return useFlagMutation(
        (input: CreateFlagInput) => createFlag(input),
        (input) => input.key,
    );
}

export function useUpdateFlag(key: string) {
    return useFlagMutation(
        (input: UpdateFlagInput) => updateFlag(key, input),
        () => key,
    );
}

export function useSetDefault(key: string) {
    return useFlagMutation(
        (input: SetDefaultInput) => setDefault(key, input),
        () => key,
    );
}

export function useSetEnvironmentDefault(key: string) {
    return useFlagMutation(
        (args: { environment: FlagEnvironment } & SetEnvironmentDefaultInput) => {
            const { environment, ...input } = args;
            return setEnvironmentDefault(key, environment, input);
        },
        () => key,
    );
}

export function useClearEnvironmentDefault(key: string) {
    return useFlagMutation(
        (args: { environment: FlagEnvironment; reason?: string }) =>
            clearEnvironmentDefault(key, args.environment, args.reason),
        () => key,
    );
}

export function useSetOverride(key: string) {
    return useFlagMutation(
        (args: { tenantId: string } & SetOverrideInput) => {
            const { tenantId, ...input } = args;
            return setOverride(key, tenantId, input);
        },
        () => key,
        (args) => [tenantFlagKeys.list(args.tenantId)],
    );
}

export function useClearOverride(key: string) {
    return useFlagMutation(
        (args: { tenantId: string; reason?: string }) =>
            clearOverride(key, args.tenantId, args.reason),
        () => key,
        (args) => [tenantFlagKeys.list(args.tenantId)],
    );
}

/* ---------------------------------------------------------- one museum - */

/**
 * The same overrides, read from the museum instead of from the flag.
 *
 * Its own key space rather than a slice of `flagKeys`: the route is a property
 * of a tenant, the response is the whole catalog joined against that tenant's
 * override partition, and nothing in it can be derived from a cached flag list.
 */
export const tenantFlagKeys = {
    all: ['tenant-flags'] as const,
    list: (tenantId: string) => [...tenantFlagKeys.all, tenantId] as const,
};

export function useTenantFlags(tenantId: string) {
    return useQuery({
        queryKey: tenantFlagKeys.list(tenantId),
        queryFn: ({ signal }) => listTenantFlags(tenantId, signal),
        // An id that resolves for nobody is a real state on this screen: the
        // clients list and the flags table are filled from different stores,
        // and a tenant present in one can be absent from the other.
        enabled: tenantId.length > 0,
        retry: retryUnlessClientError,
        networkMode: 'always',
    });
}

/**
 * Set and clear an override from the museum's side.
 *
 * Deliberately the same two writes the flag screen uses — `PUT`/`DELETE
 * /flags/:key/overrides/:tenantId` — rather than a second pair of routes aimed
 * at the tenant. One write path per row type keeps the audit trail to one shape
 * per action, however the operator got there.
 */
export function useSetTenantOverride(tenantId: string) {
    return useFlagMutation(
        (args: { key: string } & SetOverrideInput) => {
            const { key, ...input } = args;
            return setOverride(key, tenantId, input);
        },
        (args) => args.key,
        () => [tenantFlagKeys.list(tenantId)],
    );
}

export function useClearTenantOverride(tenantId: string) {
    return useFlagMutation(
        (args: { key: string; reason?: string }) => clearOverride(args.key, tenantId, args.reason),
        (args) => args.key,
        () => [tenantFlagKeys.list(tenantId)],
    );
}
